import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import CardCarousel from '@homeaway/react-card-carousel';
import VisibilitySensor from 'react-visibility-sensor';
import {AspectRatioCard} from '@homeaway/react-aspect-ratio-card';

import useEdapEvent from '../../../hooks/useEdapEvent';
import {SeeMoreButton} from '../ViewMoreButton/ViewMoreButton';
import {DEFAULT_TRANSLATIONS} from '../../../utils/constants';
import {
    CAROUSEL_HIT_ID_PATH,
    CAROUSEL_TYPES,
    EVENT_TYPES,
} from '../../../utils/edap/edapEventsConstants';
import {get} from '../../../utils/helpers';

import './Carousel.less';

const aspectRatioValue = '3x2';

// Centers the controls within the image of carousel.
/* istanbul ignore next */
const getControlOffset = (aspectRatio) => {
    return (width, controlHeight) => {
        return AspectRatioCard.getControlOffset(
            width,
            controlHeight,
            aspectRatio
        );
    };
};

const getHitsInView = (hits = [], startIndex, endIndex) =>
    hits.slice(startIndex, endIndex);

const getHitsInViewIds = (hitsInView, hitIdPath) =>
    hitsInView.map((hit) => get(hit, hitIdPath, ''));

export default function Carousel(props) {
    const {
        breakpoints,
        cards,
        cardSizeSm,
        carouselType,
        className,
        clientRequestId,
        hits,
        isMobile,
        layoutAlignment,
        onCardsShownChange,
        propertyViewEventFirePosition,
        scrolling,
        scrollMarginOffset,
        searchUrl,
        seeMoreButton,
        subheading,
        target,
        title,
        translations,
    } = props;

    const [currentIndex, setCurrentIndex] = useState(0);
    const [wasShownOnce, setWasShownOnce] = useState(false);
    const [numberCardsVisible, setNumberCardsVisible] = useState(0);
    const edapEvents = useEdapEvent(carouselType);

    const hitsInView = getHitsInView(
        hits,
        currentIndex,
        currentIndex + numberCardsVisible
    );

    const eventTitle =
        typeof title === 'object'
            ? title.props.ownerDetails.label || ''
            : title;

    const hitsInViewIds = getHitsInViewIds(
        hitsInView,
        CAROUSEL_HIT_ID_PATH[carouselType]
    );

    useEffect(() => {
        if (
            hitsInView.length > 0 &&
            propertyViewEventFirePosition &&
            propertyViewEventFirePosition === 'onload'
        ) {
            edapEvents[EVENT_TYPES.PRESENTED]({
                target,
                title: eventTitle,
                hitsInView: JSON.stringify(hitsInView),
                hitsInViewIds,
                clientRequestId,
            });
            setWasShownOnce(true);
        }
    }, [hitsInView.length]);

    if (!cards || !cards.length) {
        return null;
    }

    const onCardIndexChange = (event, newIndex) => {
        setCurrentIndex(newIndex);
    };

    const onCardsShownChangeHandler = (newCardIndex, allIndicesInView) => {
        onCardsShownChange(newCardIndex, allIndicesInView);
        if (wasShownOnce) {
            edapEvents[EVENT_TYPES.UPDATED]({
                clientRequestId,
                hitsInViewIds,
                hitsInView: JSON.stringify(hitsInView),
            });
        }
    };

    const onEnter = (isVisible) => {
        if (
            isVisible &&
            hitsInView.length > 0 &&
            propertyViewEventFirePosition &&
            propertyViewEventFirePosition !== 'onload'
        ) {
            edapEvents[EVENT_TYPES.PRESENTED]({
                target,
                title: eventTitle,
                hitsInView: JSON.stringify(hitsInView),
                hitsInViewIds,
                clientRequestId,
            });
            setWasShownOnce(true);
        }
    };

    const onResizeHandler = (carouselParams) => {
        setNumberCardsVisible(carouselParams.numberCardsVisible);
    };

    const classNameWrap = cx(className, {
        'recommended-carousel': true,
        'recommended-carousel--sm': cardSizeSm,
    });

    const showTopSeeMoreButton = seeMoreButton && !isMobile;
    const showBottomSeeMoreButton = seeMoreButton && isMobile;

    const seeMoreButtonProps = {
        searchUrl,
        translations,
        isMobile,
    };

    const visibilitySensorSettings = {
        active: !wasShownOnce,
        onChange: onEnter,
    };

    return (
        <VisibilitySensor {...visibilitySensorSettings}>
            <div className={classNameWrap}>
                {showTopSeeMoreButton && (
                    <div className="recommended-carousel__button-row">
                        <div className="recommended-carousel__button-container">
                            <SeeMoreButton {...seeMoreButtonProps} />
                        </div>
                    </div>
                )}
                <CardCarousel
                    cardIndex={currentIndex}
                    getControlOffset={getControlOffset(aspectRatioValue)}
                    nextLabel={translations.nextButtonLabel}
                    onCardsShownChange={onCardsShownChangeHandler}
                    onNextClick={onCardIndexChange}
                    onPreviousClick={onCardIndexChange}
                    onResize={onResizeHandler}
                    paging
                    previousLabel={translations.prevButtonLabel}
                    {...{
                        breakpoints,
                        layoutAlignment,
                        scrolling,
                        subheading,
                        title,
                        scrollMarginOffset,
                    }}
                >
                    {cards}
                </CardCarousel>
                {showBottomSeeMoreButton && (
                    <SeeMoreButton {...seeMoreButtonProps} />
                )}
            </div>
        </VisibilitySensor>
    );
}

Carousel.propTypes = {
    breakpoints: PropTypes.objectOf(PropTypes.number),
    cards: PropTypes.arrayOf(PropTypes.object),
    cardSizeSm: PropTypes.bool,
    carouselType: PropTypes.oneOf([
        CAROUSEL_TYPES.PROPERTY,
        CAROUSEL_TYPES.DESTINATION,
    ]),
    className: PropTypes.string,
    clientRequestId: PropTypes.string,
    hits: PropTypes.arrayOf(PropTypes.object),
    isMobile: PropTypes.bool,
    layoutAlignment: PropTypes.string,
    onCardsShownChange: PropTypes.func,
    scrolling: PropTypes.bool,
    scrollMarginOffset: PropTypes.number,
    searchUrl: PropTypes.string,
    seeMoreButton: PropTypes.bool,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    subheading: PropTypes.string,
    propertyViewEventFirePosition: PropTypes.string,
    translations: PropTypes.shape({
        nextButtonLabel: PropTypes.string,
        prevButtonLabel: PropTypes.string,
        seeMore: PropTypes.string,
    }),
    target: PropTypes.string,
};

Carousel.defaultProps = {
    cards: [],
    hits: [],
    className: '',
    translations: {
        nextButtonLabel: DEFAULT_TRANSLATIONS.nextButtonLabel,
        prevButtonLabel: DEFAULT_TRANSLATIONS.prevButtonLabel,
        seeMore: DEFAULT_TRANSLATIONS.seeMore,
    },
    onCardsShownChange: () => {},
};
